import React, { useState } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { FULFILMENT, PAYMENT_TONE, fmtMoney, fmtDateTime } from './productCommerce';
import './ProductV2.css';
import { can } from '../permissions/access';

// Order fulfilment detail — slide-out from the Orders tab.
//
// Status is fully REVERSIBLE: the stepper lets you move an order forward or
// back to any stage (pack -> ship -> deliver, or ready -> collected), so a
// mistaken "packed" can be undone. Plus editable tracking, a private staff
// note, customer-email-on-ship, print and cancel. Mock-first; onUpdate persists.
//
// TODO(backend): shipping / "ready" emails are sent server-side; partial
// fulfilment (some items now, rest later), refunds, and bulk actions on the
// list are the next additions.

const StatusPill = ({ tone, label }) => (
  <span className={`pv2-pill pv2-pill--${tone}`}>{label}</span>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 8.5l3.2 3.2L13 5" /></svg>
);

// Linear fulfilment stages by delivery method. cancelled / refunded sit off-flow.
const SHIP_STAGES = ['unfulfilled', 'packed', 'shipped', 'delivered'];
const COLLECT_STAGES = ['unfulfilled', 'ready_for_collection', 'collected'];

// Stepper accent matches the status tone (and the kanban columns / status pills).
const TONE_COLOR = { amber: '#E0A33C', blue: '#3B82C4', violet: '#7C6BD6', green: '#1FA97A', grey: '#aab0b6' };
const TONE_SOFT = { amber: 'rgba(224,163,60,0.16)', blue: 'rgba(59,130,196,0.16)', violet: 'rgba(124,107,214,0.16)', green: 'rgba(31,169,122,0.16)', grey: 'rgba(170,176,182,0.16)' };
const SHORT = {
  unfulfilled: 'Unfulfilled', packed: 'Packed', shipped: 'Shipped', delivered: 'Delivered',
  ready_for_collection: 'Ready', collected: 'Collected',
};
const paymentLabel = (s) => (s === 'paid' ? 'Paid' : s === 'partially_refunded' ? 'Part refunded' : s === 'refunded' ? 'Refunded' : s === 'pending' ? 'Pending' : s);

const ProductOrderDetail = ({ order, currency = '£', onClose, onUpdate, onCancel }) => {
  const [shipForm, setShipForm] = useState(null);     // { courier, number, notify, mode: 'ship' | 'edit' }
  const [noteDraft, setNoteDraft] = useState((order && order.internalNote) || '');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  if (!order) return null;

  const isShip = order.delivery && order.delivery.method === 'ship';
  const stages = isShip ? SHIP_STAGES : COLLECT_STAGES;
  const cancelled = order.fulfilmentStatus === 'cancelled';
  const curIdx = stages.indexOf(order.fulfilmentStatus);
  const f = FULFILMENT[order.fulfilmentStatus] || FULFILMENT.unfulfilled;
  const hasTracking = order.tracking && (order.tracking.courier || order.tracking.number);
  const nextStage = curIdx >= 0 && curIdx < stages.length - 1 ? stages[curIdx + 1] : null;
  const isPaid = order.paymentStatus === 'paid';
  // Cancelling a PAID order refunds the customer — `refunds` on the server
  // (access audit 2026-10-02). Unpaid orders just cancel.
  const canCancel = !['delivered', 'collected', 'cancelled'].includes(order.fulfilmentStatus) && typeof onCancel === 'function'
    && (!isPaid || can('refunds'));

  const apply = (patch, label) => onUpdate(order.id, patch, label);

  // Move to any stage — forward or back. Going to "shipped" with no tracking
  // opens the tracking form first.
  const moveTo = (target) => {
    if (target === order.fulfilmentStatus) return;
    if (target === 'shipped' && !hasTracking) { setShipForm({ courier: '', number: '', notify: true, mode: 'ship' }); return; }
    const forward = stages.indexOf(target) > curIdx;
    apply({ fulfilmentStatus: target }, `${forward ? 'Marked as' : 'Moved back to'} ${(FULFILMENT[target].label || '').toLowerCase()}`);
  };

  const confirmShip = () => {
    const courier = (shipForm.courier || '').trim();
    const number = (shipForm.number || '').trim();
    const tracking = courier || number ? { courier, number, url: '' } : null;
    if (shipForm.mode === 'edit') {
      apply({ tracking }, 'Tracking updated');
    } else {
      const notified = shipForm.notify ? ' · customer emailed' : '';
      apply({ fulfilmentStatus: 'shipped', tracking }, `Shipped${courier ? ` via ${courier}` : ''}${number ? ` · ${number}` : ''}${notified}`);
    }
    setShipForm(null);
  };

  const saveNote = () => {
    if ((order.internalNote || '') !== noteDraft) apply({ internalNote: noteDraft }, null);
  };

  const headerRight = (
    <div className="pv2-pill-row">
      <StatusPill tone={PAYMENT_TONE[order.paymentStatus] || 'grey'} label={paymentLabel(order.paymentStatus)} />
      <StatusPill tone={f.tone} label={f.label} />
    </div>
  );

  const footer = (
    <>
      <button type="button" className="pv2-btn pv2-btn--ghost" onClick={() => window.print()}>Print</button>
      {!cancelled && nextStage && (
        <button type="button" className="pv2-btn pv2-btn--primary" onClick={() => moveTo(nextStage)}>
          Mark as {(FULFILMENT[nextStage].label || '').toLowerCase()}
        </button>
      )}
      {cancelled && (
        <button type="button" className="pv2-btn pv2-btn--primary" onClick={() => apply({ fulfilmentStatus: 'unfulfilled' }, 'Order reopened')}>
          Reopen order
        </button>
      )}
    </>
  );

  return (
    <SlidePanel title={`Order ${order.number}`} onClose={onClose} headerRight={headerRight} footer={footer} width={520} className="pv2-order-detail">
      {/* Fulfilment — reversible stepper */}
      <div className="pv2-od-section">
        <p className="pv2-od-label">Fulfilment</p>
        {cancelled ? (
          <div className="pv2-od-cancelled">This order was cancelled. Reopen it to continue fulfilling.</div>
        ) : (
          <>
            <div className="pv2-od-stepper" role="group" aria-label="Fulfilment status" style={{ '--od-accent': TONE_COLOR[f.tone] || '#007EA0', '--od-accent-soft': TONE_SOFT[f.tone] || 'rgba(0,126,160,0.12)' }}>
              {stages.map((s, i) => {
                const state = i < curIdx ? 'done' : i === curIdx ? 'current' : 'todo';
                return (
                  <button key={s} type="button" className={`pv2-od-step is-${state}`} onClick={() => moveTo(s)} aria-current={state === 'current' ? 'step' : undefined}>
                    <span className="pv2-od-step-dot">{state === 'done' ? <CheckIcon /> : i + 1}</span>
                    <span className="pv2-od-step-label">{SHORT[s]}</span>
                  </button>
                );
              })}
            </div>
            <p className="pv2-od-step-hint">Tap any stage to move this order forward or back.</p>
          </>
        )}
      </div>

      {/* Tracking form (ship now / edit) */}
      {shipForm && (
        <div className="pv2-od-section pv2-od-shipform">
          <p className="pv2-od-label">{shipForm.mode === 'edit' ? 'Edit tracking' : 'Add tracking (optional)'}</p>
          <input className="pv2-input" placeholder="Courier (e.g. Royal Mail)" value={shipForm.courier} onChange={(e) => setShipForm({ ...shipForm, courier: e.target.value })} />
          <input className="pv2-input" placeholder="Tracking number" value={shipForm.number} onChange={(e) => setShipForm({ ...shipForm, number: e.target.value })} />
          {shipForm.mode !== 'edit' && (
            <label className="pv2-od-check">
              <input type="checkbox" checked={shipForm.notify} onChange={(e) => setShipForm({ ...shipForm, notify: e.target.checked })} />
              <span>Email tracking to {order.customer.name}</span>
            </label>
          )}
          <div className="pv2-od-shipform-actions">
            <button type="button" className="pv2-btn pv2-btn--ghost" onClick={() => setShipForm(null)}>Cancel</button>
            <button type="button" className="pv2-btn pv2-btn--primary" onClick={confirmShip}>{shipForm.mode === 'edit' ? 'Save tracking' : 'Confirm shipped'}</button>
          </div>
        </div>
      )}

      {/* Delivery */}
      <div className="pv2-od-section">
        <p className="pv2-od-label">{isShip ? 'Ship to' : 'Collect in salon'}</p>
        <div className="pv2-od-person">
          <div className="pv2-od-name">{order.customer.name}</div>
          <div className="pv2-od-meta">{order.customer.email}</div>
          {order.customer.phone && <div className="pv2-od-meta">{order.customer.phone}</div>}
          {isShip && order.delivery.address && (
            <div className="pv2-od-address">
              {order.delivery.address.line1}, {order.delivery.address.city}, {order.delivery.address.postcode}
              <br />{order.delivery.address.country}
            </div>
          )}
          {!isShip && <div className="pv2-od-meta">{(order.delivery && order.delivery.instructions) || 'Collect from reception'}</div>}
        </div>
      </div>

      {order.customerNote && (
        <div className="pv2-od-note">“{order.customerNote}”</div>
      )}

      {/* Tracking (existing) */}
      {!shipForm && hasTracking && (
        <div className="pv2-od-section">
          <div className="pv2-od-label-row">
            <p className="pv2-od-label">Tracking</p>
            <div className="pv2-od-label-actions">
              <button type="button" className="pv2-od-textbtn" onClick={() => setShipForm({ courier: order.tracking.courier || '', number: order.tracking.number || '', mode: 'edit' })}>Edit</button>
              <button type="button" className="pv2-od-textbtn" onClick={() => apply({ tracking: null }, 'Tracking removed')}>Remove</button>
            </div>
          </div>
          <div className="pv2-od-rows">
            {order.tracking.courier && <div className="pv2-od-row"><span>Courier</span><strong>{order.tracking.courier}</strong></div>}
            {order.tracking.number && <div className="pv2-od-row"><span>Number</span><strong>{order.tracking.number}</strong></div>}
          </div>
        </div>
      )}
      {!shipForm && !hasTracking && isShip && ['shipped', 'delivered'].includes(order.fulfilmentStatus) && (
        <div className="pv2-od-section">
          <button type="button" className="pv2-od-textbtn" onClick={() => setShipForm({ courier: '', number: '', mode: 'edit' })}>+ Add tracking</button>
        </div>
      )}

      {/* Items */}
      <div className="pv2-od-section">
        <p className="pv2-od-label">Items</p>
        <div className="pv2-od-items">
          {order.items.map((it, i) => (
            <div key={i} className="pv2-od-item">
              <span className="pv2-od-item-qty">{it.qty}×</span>
              <span className="pv2-od-item-name">
                {it.name}
                {it.qtyFulfilled >= it.qty && <span className="pv2-od-item-tag">Fulfilled</span>}
              </span>
              <span className="pv2-od-item-price">{fmtMoney(it.qty * it.unitPrice, currency)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="pv2-od-section">
        <div className="pv2-od-rows">
          <div className="pv2-od-row"><span>Subtotal</span><strong>{fmtMoney(order.subtotal, currency)}</strong></div>
          {order.discount > 0 && <div className="pv2-od-row"><span>Discount</span><strong>-{fmtMoney(order.discount, currency)}</strong></div>}
          <div className="pv2-od-row"><span>{isShip ? 'Shipping' : 'Collection'}</span><strong>{order.shipping > 0 ? fmtMoney(order.shipping, currency) : 'Free'}</strong></div>
          {order.tax > 0 && <div className="pv2-od-row"><span>Tax</span><strong>{fmtMoney(order.tax, currency)}</strong></div>}
          <div className="pv2-od-row pv2-od-row--total"><span>Total paid</span><strong>{fmtMoney(order.total, currency)}</strong></div>
        </div>
      </div>

      {/* Staff note (private) */}
      <div className="pv2-od-section">
        <p className="pv2-od-label">Staff note</p>
        <textarea
          className="pv2-od-note-input"
          placeholder="Add a private note for your team…"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={saveNote}
          rows={2}
        />
      </div>

      {/* Timeline */}
      <div className="pv2-od-section">
        <p className="pv2-od-label">Timeline</p>
        <div className="pv2-od-timeline">
          {[...(order.timeline || [])].reverse().map((t, i) => (
            <div key={i} className="pv2-od-tl">
              <span className="pv2-od-tl-dot" aria-hidden />
              <div>
                <div className="pv2-od-tl-label">{t.label}</div>
                <div className="pv2-od-tl-time">{fmtDateTime(t.at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel — paid orders are refunded in full and the payout is reclaimed
          from the salon (incl. fees), so the confirm step spells that out. */}
      {canCancel && !cancelConfirm && (
        <div className="pv2-od-secondary">
          <button type="button" className="pv2-od-textbtn pv2-od-textbtn--danger" onClick={() => setCancelConfirm(true)}>Cancel order</button>
        </div>
      )}
      {canCancel && cancelConfirm && (
        <div className="pv2-od-cancelconfirm">
          <div className="pv2-od-cancelconfirm-title">Cancel order {order.number}?</div>
          <p className="pv2-od-cancelconfirm-text">
            {isPaid
              ? `${currency}${(order.total || 0).toFixed(2)} will be refunded to the customer. The full amount, including processing fees, is reclaimed from your account, and tracked stock is returned.`
              : 'This order has not been paid — it will simply be cancelled.'}
          </p>
          <div className="pv2-od-cancelconfirm-actions">
            <button
              type="button"
              className="pv2-od-textbtn pv2-od-textbtn--danger"
              disabled={cancelBusy}
              onClick={() => {
                setCancelBusy(true);
                onCancel(order.id, '')
                  .then((result) => {
                    setCancelBusy(false);
                    setCancelConfirm(false);
                    if (result && result.refundErrors) {
                      alert('Order cancelled and the customer refunded, but part of the money reversal needs attention — Beu support has been flagged.');
                    }
                    onClose();
                  })
                  .catch((err) => {
                    setCancelBusy(false);
                    const msg = err?.response?.data?.messages || 'Cancellation failed — the customer has NOT been charged or refunded. Try again or contact Beu support.';
                    alert(msg);
                  });
              }}
            >
              {cancelBusy ? 'Cancelling…' : (isPaid ? `Cancel & refund ${currency}${(order.total || 0).toFixed(2)}` : 'Cancel order')}
            </button>
            <button type="button" className="pv2-od-textbtn" disabled={cancelBusy} onClick={() => setCancelConfirm(false)}>Keep order</button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
};

export default ProductOrderDetail;
